import React from "react";
import { useSelector } from "react-redux";
import ModalHeader from "./ModalHeader";

export const ModalFullScreenContext = React.createContext(false);

// Size tokens match the standard widths used across the app. Pass __modalSize in the Redux properties when dispatching setModalState.
export type ModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

export const MODAL_SIZE_WIDTHS: Record<ModalSize, string> = {
  xs: "360px",
  sm: "480px",
  md: "710px",
  lg: "900px",
  xl: "1110px",
  "2xl": "1200px",
};

interface ModalBaseProps {
  title: string;
  icon?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  className?: string;
  dividerClassName?: string;
  fillHeight?: boolean;
  // Small inline element after the title, such as a status pill
  titleAdornment?: React.ReactNode;
  // Overrides the footer's top padding (default pt-4)
  footerPaddingClassName?: string;
}

/** CREATING A NEW MODAL — follow these steps every time: 1. ADD VIEW CONSTANT In src/store/modalSlice.ts → modalViewsConst, add: myModal: "myModal" 2. CREATE THE COMPONENT New file: src/components/Modal/MyModal.tsx - Import and use ModalBase (this file) — do NOT use Dialog/Transition directly - Accept `onClose?: (result?: any) => void` as a prop - ModalBase handles the header, scrollable content area, and pinned footer - icon prop is optional — omit it if the modal has no icon 3. REGISTER IN ModalGlobal In src/components/Modal/ModalGlobal.tsx → MODAL_COMPONENTS, add: [modalViewsConst.myModal]: MyModal Use React.lazy() for large modals. 4. OPEN THE MODAL dispatch(setModalState({ modalOpen: true, modalView: modalViewsConst.myModal, properties: { __modalSize: 'md', ...yourData }, })); Size tokens → widths: xs=360px, sm=480px, md=710px, lg=900px, xl=1110px, 2xl=1200px Always pass __modalSize — ModalGlobal uses it to compute the full-screen breakpoint. Note: "wideMap" is a legacy __modalSize value — do not use it in new modals. WHAT YOU GET FOR FREE (from ModalGlobal + ModalBase): - Pinned header and footer, scrollable content area - Full-screen mode when the modal width approaches the viewport edges - Mobile full-screen behavior - Open/close animations FOOTER BUTTONS - xs/sm: buttons stretch full width (flex-1) — suited for compact, single-action modals - md/lg/xl/2xl: buttons are right-aligned — suited for forms and data-heavy modals */
export const SMALL_SIZES: ModalSize[] = ["xs", "sm"];

const ModalBase: React.FC<ModalBaseProps> = ({
  title,
  icon,
  onClose,
  children,
  footer,
  size = "md",
  className = "",
  dividerClassName = "",
  fillHeight = false,
  titleAdornment,
  footerPaddingClassName = "pt-4",
}) => {
  const isFullScreen = React.useContext(ModalFullScreenContext);
  // Full-screen (mobile) stretches the footer buttons — a right-aligned pill is a small target on a phone
  const footerAlign =
    SMALL_SIZES.includes(size) || isFullScreen
      ? "flex space-x-3 [&>*]:flex-1"
      : "flex space-x-3 justify-end";
  const heightClass = isFullScreen ? "h-dvh" : fillHeight ? "h-[90dvh]" : "max-h-[90dvh]";

  // ModalGlobal reuses one component across views (add vs edit asset), so this scroller survives the swap and the next modal opens where the last was left. Keyed on the view, not the title — an in-modal sub-view (the asset map) keeps its own view, so returning to the form holds place.
  const modalView = useSelector((state: any) => state.modalState?.modalView);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, left: 0 });
  }, [modalView]);

  return (
    <div className={`flex flex-col w-full ${heightClass} pt-5 ${className}`}>
      <ModalHeader
        title={title}
        icon={icon}
        titleAdornment={titleAdornment}
        onClose={onClose}
        className="!border-b-0"
        gutterClassName="eai-modal-gutter"
      />
      {/* The rule is inset by the gutter, not by a margin of its own, so it tracks the body */}
      <div className="eai-modal-gutter">
        <div className={`border-b border-eai-border ${dividerClassName}`} />
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <div ref={bodyRef} className="scrollbar-hover eai-modal-gutter flex-1 min-h-0 overflow-y-auto py-5">{children}</div>
        {footer && <div className={`${footerAlign} eai-modal-gutter pb-5 sm:pb-10 ${footerPaddingClassName}`}>{footer}</div>}
      </div>
    </div>
  );
};

export default ModalBase;
